import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase.js";
import { INDUSTRIES, industryLabel, isKnownIndustry } from "../lib/industries.js";
import { Button, Input, Select } from "../lib/ui.js";
import { AU_STATES } from "../lib/phone.js";
import { timeZoneForState, timeZoneLabel } from "../lib/timeZone.js";

interface BusinessRow {
  id: string;
  name: string;
  industry: string | null;
  state: string | null;
  timezone: string | null;
  clients: { name: string } | null;
}

/**
 * Each business's name and which kind of business it is. The platform admin sets both here (a business cannot change its own
 * industry or, through the portal, its name). The name is what the receptionist says in the greeting; the industry decides which
 * industry rules that business's receptionist follows (admin > Industry rules). A value outside the list, such as "test", is shown
 * as it is and stays until it is changed.
 */
export function BusinessIndustries() {
  const [rows, setRows] = useState<BusinessRow[]>([]);
  const [saving, setSaving] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState("");

  useEffect(() => {
    void supabase
      .from("businesses")
      .select("id, name, industry, state, timezone, clients(name)")
      .order("name")
      .then(({ data }) => setRows((data ?? []) as unknown as BusinessRow[]));
  }, []);

  async function change(row: BusinessRow, industry: string) {
    setSaving(row.id);
    setError(null);
    setMessage(null);
    const { error: err } = await supabase.rpc("rpc_admin_set_business_industry", { p_business_id: row.id, p_industry: industry });
    setSaving(null);
    if (err) {
      setError(err.message.includes("industry_invalid") ? "That isn't a valid industry." : err.message);
      return;
    }
    setRows((all) => all.map((r) => (r.id === row.id ? { ...r, industry: industry || null } : r)));
    setMessage(`${row.name} is now ${industry ? industryLabel(industry) : "not set"}.`);
  }

  async function changeState(row: BusinessRow, state: string) {
    setSaving(row.id);
    setError(null);
    setMessage(null);
    const { error: err } = await supabase.rpc("rpc_admin_set_business_state", { p_business_id: row.id, p_state: state });
    setSaving(null);
    if (err) {
      setError(err.message.includes("state_invalid") ? "That isn't an Australian state or territory." : err.message);
      return;
    }
    // The database sets the time zone from the state; show what it did.
    const zone = timeZoneForState(state);
    setRows((all) => all.map((r) => (r.id === row.id ? { ...r, state: state || null, timezone: zone ?? r.timezone } : r)));
    setMessage(
      state
        ? `${row.name} is in ${state}: it now runs on ${timeZoneLabel(zone ?? row.timezone ?? "Australia/Sydney")}. Bookings, opening hours and the receptionist's clock follow it (calls pick it up within about a minute).`
        : `${row.name} has no state set. It keeps ${timeZoneLabel(row.timezone ?? "Australia/Sydney")}.`
    );
  }

  async function rename(row: BusinessRow) {
    const next = nameDraft.replace(/\s+/g, " ").trim();
    if (next === row.name) {
      setEditingId(null);
      return;
    }
    if (!next || next.length > 100) {
      setError("A business name needs to be between 1 and 100 characters.");
      return;
    }
    setSaving(row.id);
    setError(null);
    setMessage(null);
    const { error: err } = await supabase.rpc("rpc_admin_rename_business", { p_business_id: row.id, p_name: next });
    setSaving(null);
    if (err) {
      setError(err.message.includes("business_name_invalid") ? "A business name needs to be between 1 and 100 characters." : err.message.includes("not_authorized") ? "Only a platform admin can rename a business." : err.message);
      return;
    }
    setRows((all) => all.map((r) => (r.id === row.id ? { ...r, name: next } : r)));
    setEditingId(null);
    setMessage(`Renamed to ${next}. Calls that start from now on use the new name (within about a minute).`);
  }

  return (
    <section style={{ marginTop: 32 }}>
      <h2 className="rc-h2" style={{ marginBottom: 4 }}>Businesses</h2>
      <p style={{ color: "var(--rc-text-muted)", fontSize: 13, margin: "0 0 12px" }}>
        The name is what the receptionist says when it answers. The industry decides which industry rules it follows (see Industry rules). The state decides the
        business's time zone, which every booking, opening hour and clock follows. Only you can change them.
      </p>
      <div className="rc-table-scroll">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid var(--rc-border)", color: "var(--rc-text-muted)" }}>
              <th style={{ padding: "10px 12px" }}>Business</th>
              <th style={{ padding: "10px 12px" }}>Client</th>
              <th style={{ padding: "10px 12px" }}>Industry</th>
              <th style={{ padding: "10px 12px" }}>State and time zone</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
                <td style={{ padding: "10px 12px" }}>
                  {editingId === r.id ? (
                    <span style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <Input
                        aria-label={`Name for ${r.name}`}
                        value={nameDraft}
                        maxLength={100}
                        autoFocus
                        disabled={saving === r.id}
                        onChange={(e) => setNameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void rename(r);
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        style={{ minWidth: 220 }}
                      />
                      <Button variant="primary" onClick={() => rename(r)} disabled={saving === r.id || !nameDraft.trim()}>
                        Save
                      </Button>
                      <Button variant="secondary" onClick={() => setEditingId(null)} disabled={saving === r.id}>
                        Cancel
                      </Button>
                    </span>
                  ) : (
                    <span style={{ display: "inline-flex", gap: 10, alignItems: "baseline" }}>
                      {r.name}
                      <button
                        aria-label={`Rename ${r.name}`}
                        style={{ background: "none", border: "none", padding: 0, color: "var(--rc-primary)", cursor: "pointer", fontSize: 13 }}
                        onClick={() => {
                          setEditingId(r.id);
                          setNameDraft(r.name);
                          setError(null);
                          setMessage(null);
                        }}
                      >
                        Rename
                      </button>
                    </span>
                  )}
                </td>
                <td style={{ padding: "10px 12px" }}>{r.clients?.name ?? "-"}</td>
                <td style={{ padding: "10px 12px" }}>
                  <Select
                    aria-label={`Industry for ${r.name}`}
                    value={r.industry ?? ""}
                    disabled={saving === r.id}
                    onChange={(e) => change(r, e.target.value)}
                  >
                    <option value="">Not set</option>
                    {INDUSTRIES.map((i) => (
                      <option key={i.value} value={i.value}>
                        {i.label}
                      </option>
                    ))}
                    {r.industry && !isKnownIndustry(r.industry) && <option value={r.industry}>{r.industry} (custom)</option>}
                  </Select>
                </td>
                <td style={{ padding: "10px 12px" }}>
                  <Select aria-label={`State for ${r.name}`} value={r.state ?? ""} disabled={saving === r.id} onChange={(e) => changeState(r, e.target.value)}>
                    <option value="">Not set</option>
                    {AU_STATES.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                    {r.state && !(AU_STATES as readonly string[]).includes(r.state) && <option value={r.state}>{r.state} (not recognised)</option>}
                  </Select>
                  <div style={{ fontSize: 12.5, color: "var(--rc-text-muted)", marginTop: 4 }}>{r.timezone ? timeZoneLabel(r.timezone) : "-"}</div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} style={{ padding: 16, color: "var(--rc-text-faint)" }}>
                  No businesses yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {error && <p style={{ color: "var(--rc-danger)", marginTop: 12 }}>{error}</p>}
      {message && <p style={{ color: "var(--rc-success)", marginTop: 12 }}>{message}</p>}
    </section>
  );
}
